import apiClient from './client';

// =============================================
// Types
// =============================================
export type RecurringInterval = 'weekly' | 'monthly' | 'quarterly' | 'yearly';

export interface RecurringExpense {
  id: string;
  name: string;
  amount: number;
  interval: RecurringInterval;
  next_due_date: string; // ISO date string YYYY-MM-DD
  category?: string;
  icon?: string;
  note?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RecurringExpenseCreate {
  name: string;
  amount: number;
  interval: RecurringInterval;
  next_due_date: string;
  category?: string;
  icon?: string;
  note?: string;
}

export interface RecurringExpenseUpdate {
  name?: string;
  amount?: number;
  interval?: RecurringInterval;
  next_due_date?: string;
  category?: string;
  icon?: string;
  note?: string;
  is_active?: boolean;
}

export interface RecurringExpenseSummary {
  monthly_total: number;
  yearly_total: number;
  count: number;
  due_soon_count: number; // Due within 7 days
}

// =============================================
// API
// =============================================
export const recurringExpensesApi = {
  /**
   * GET /recurring-expenses — Alle Ausgaben des aktuellen Benutzers
   */
  list: async (): Promise<RecurringExpense[]> => {
    const response = await apiClient.get<RecurringExpense[]>('/recurring-expenses');
    return response.data.map((e) => ({ ...e, amount: Number(e.amount) }));
  },

  /**
   * GET /recurring-expenses/{id} — Einzelne Ausgabe
   */
  get: async (id: string): Promise<RecurringExpense> => {
    const response = await apiClient.get<RecurringExpense>(`/recurring-expenses/${id}`);
    return response.data;
  },

  /**
   * POST /recurring-expenses — Neue Ausgabe anlegen
   */
  create: async (data: RecurringExpenseCreate): Promise<RecurringExpense> => {
    const response = await apiClient.post<RecurringExpense>('/recurring-expenses', data);
    return response.data;
  },

  /**
   * PUT /recurring-expenses/{id} — Ausgabe aktualisieren
   */
  update: async (id: string, data: RecurringExpenseUpdate): Promise<RecurringExpense> => {
    const response = await apiClient.put<RecurringExpense>(`/recurring-expenses/${id}`, data);
    return response.data;
  },

  /**
   * DELETE /recurring-expenses/{id} — Ausgabe loeschen
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/recurring-expenses/${id}`);
  },

  /**
   * GET /recurring-expenses/summary — Monats-/Jahrestotals und faellige Ausgaben
   */
  getSummary: async (): Promise<RecurringExpenseSummary> => {
    const response = await apiClient.get<RecurringExpenseSummary>('/recurring-expenses/summary');
    const d = response.data;
    return {
      ...d,
      monthly_total: Number(d.monthly_total),
      yearly_total: Number(d.yearly_total),
    };
  },

  /**
   * POST /recurring-expenses/{id}/mark-paid — Ausgabe als bezahlt markieren
   * Schiebt das naechste Faelligkeitsdatum gemaess Intervall vor
   */
  markPaid: async (id: string): Promise<RecurringExpense> => {
    const response = await apiClient.post<RecurringExpense>(
      `/recurring-expenses/${id}/mark-paid`,
    );
    return response.data;
  },
};
